/**
 * Estado del sistema: qué hay instalado y si responde.
 *
 * Es un tablero de hechos, no un panel de métricas de negocio. Cada número sale
 * de una consulta real y, si esa consulta falla, la tarjeta muestra un guión y
 * el chequeo de salud dice por qué. Un tablero que pinta todo en verde porque no
 * pudo preguntar es peor que no tener tablero.
 */

import { Badge } from '@/components/shell/primitives';
import { IconCheck, IconAlert, IconSpinner } from '@/components/shell/icons';
import type { CompanyRef, TenantRef } from '@/lib/types';
import { RUTA_ROLES } from '@/panel/api';
import { describirError } from '@/panel/errores';
import { useConteo, useEmpresas, useRoles, useUsuarios } from '@/panel/queries';
import { Codigo, Etiqueta, Metrica, Panel, Vacio } from '@/panel/ui';
import { etiquetaDeModulo } from '@/panel/vocabulario';

type Salud = 'ok' | 'atencion' | 'falla' | 'cargando';

const ICONO_SALUD: Record<Salud, (props: { className?: string }) => JSX.Element> = {
  ok: IconCheck,
  atencion: IconAlert,
  falla: IconAlert,
  cargando: IconSpinner,
};

const COLOR_SALUD: Record<Salud, string> = {
  ok: 'text-success',
  atencion: 'text-warning',
  falla: 'text-danger',
  cargando: 'text-fg-faint',
};

function FilaSalud({ label, estado, detalle }: { label: string; estado: Salud; detalle: string }) {
  const Icono = ICONO_SALUD[estado];
  return (
    <li className="flex items-start gap-3 border-b border-line/60 py-2.5 last:border-b-0">
      <Icono className={`mt-0.5 h-4 w-4 shrink-0 ${COLOR_SALUD[estado]}`} />
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-fg-muted">{detalle}</p>
      </div>
    </li>
  );
}

export function EstadoScreen({
  modulos,
  companies,
  tenant,
  activeCompany,
}: {
  modulos: string[];
  companies: CompanyRef[];
  tenant: TenantRef | null;
  activeCompany: CompanyRef | null;
}) {
  const usuarios = useUsuarios();
  const roles = useRoles();
  const empresas = useEmpresas();
  const conteoContactos = useConteo('base.partner');

  const totalUsuarios = usuarios.data?.count ?? null;
  const activos = usuarios.data?.results.filter((usuario) => usuario.is_active).length ?? null;
  const totalEmpresas = empresas.data?.count ?? companies.length;

  const falloRoles = roles.error === null ? null : describirError(roles.error, RUTA_ROLES);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Metrica
          label="Módulos instalados"
          value={modulos.length}
          hint="Definen qué modelos y pantallas existen"
        />
        <Metrica
          label="Empresas"
          value={totalEmpresas}
          hint={
            companies.length === totalEmpresas
              ? 'Todas habilitadas para tu usuario'
              : `${companies.length} habilitadas para tu usuario`
          }
        />
        <Metrica
          label="Usuarios"
          value={totalUsuarios === null ? '—' : totalUsuarios}
          hint={activos === null ? 'Sin dato del servidor' : `${activos} pueden entrar hoy`}
        />
        <Metrica
          label="Roles"
          value={roles.data === undefined ? '—' : roles.data.length}
          tone={falloRoles === null ? 'neutral' : 'warning'}
          hint={falloRoles === null ? 'Definidos por los módulos' : 'El servicio no respondió'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Vistazo de salud" subtitle="Qué contestó cada servicio al abrir esta pantalla">
          <ul>
            <FilaSalud
              label="Sesión y organización"
              estado={tenant === null ? 'atencion' : 'ok'}
              detalle={
                tenant === null
                  ? 'Tu usuario no tiene organización asignada: no va a ver datos de negocio.'
                  : `Trabajando en ${tenant.name}${activeCompany === null ? '' : ` · empresa activa: ${activeCompany.name}`}`
              }
            />
            <FilaSalud
              label="Padrón de usuarios"
              estado={usuarios.isPending ? 'cargando' : usuarios.error !== null ? 'falla' : 'ok'}
              detalle={
                usuarios.isPending
                  ? 'Consultando…'
                  : usuarios.error !== null
                    ? describirError(usuarios.error, '/api/v1/model/base.user/').message
                    : `${totalUsuarios ?? 0} usuarios leídos correctamente.`
              }
            />
            <FilaSalud
              label="Empresas"
              estado={empresas.isPending ? 'cargando' : empresas.error !== null ? 'falla' : 'ok'}
              detalle={
                empresas.isPending
                  ? 'Consultando…'
                  : empresas.error !== null
                    ? describirError(empresas.error, '/api/v1/model/base.company/').message
                    : `${totalEmpresas} empresas leídas correctamente.`
              }
            />
            <FilaSalud
              label="Servicio de roles y permisos"
              estado={roles.isPending ? 'cargando' : falloRoles !== null ? 'falla' : 'ok'}
              detalle={
                roles.isPending
                  ? 'Consultando…'
                  : falloRoles !== null
                    ? `${falloRoles.title}. ${falloRoles.detail ?? ''}`
                    : `${roles.data?.length ?? 0} roles publicados por ${RUTA_ROLES}.`
              }
            />
            <FilaSalud
              label="Datos de negocio"
              estado={conteoContactos.isPending ? 'cargando' : conteoContactos.error !== null ? 'atencion' : 'ok'}
              detalle={
                conteoContactos.isPending
                  ? 'Consultando…'
                  : conteoContactos.error !== null
                    ? 'No se pudo contar los contactos: puede ser falta de permisos de tu usuario.'
                    : `${conteoContactos.data ?? 0} contactos cargados.`
              }
            />
          </ul>
        </Panel>

        <Panel title="Módulos instalados" subtitle="En el orden en que el servidor los instaló">
          {modulos.length === 0 ? (
            <Vacio title="Sin módulos" description="El servidor no informó ningún módulo instalado." />
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {modulos.map((modulo) => (
                <li key={modulo}>
                  <Badge tone="neutral" title={modulo}>
                    {etiquetaDeModulo(modulo)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4">
            <Etiqueta as="h4">Nombres técnicos</Etiqueta>
            <p className="flex flex-wrap gap-1">
              {modulos.map((modulo) => (
                <Codigo key={modulo}>{modulo}</Codigo>
              ))}
            </p>
          </div>
        </Panel>
      </div>

      <Panel title="Empresas del sistema" subtitle="Cada una lleva su contabilidad por separado">
        {empresas.isPending ? (
          <p className="text-sm text-fg-muted">Consultando…</p>
        ) : (empresas.data?.results.length ?? 0) === 0 ? (
          <Vacio title="No hay empresas cargadas" />
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {(empresas.data?.results ?? []).map((empresa) => (
              <li
                key={empresa.id}
                className="flex items-center justify-between gap-2 rounded-md border border-line px-3 py-2"
              >
                <span className="truncate text-sm">{empresa.display_name}</span>
                {activeCompany !== null && activeCompany.id === empresa.id && (
                  <Badge tone="accent">Activa</Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
